import queryString from "query-string";

import {
  SIDELOADED_BUSINESS,
  SIDELOADED_BUSINESS_SET,
  SIDELOADED_DATA_MAPPING,
} from "constants/actionTypes";

import { URLCreatorProps } from "actions/types";
import { serializeSideLoadedBusiness } from "lib/serializers/sideLoadedBusinesses";
import { serializeSideLoadedDataMapping } from "lib/serializers/sideLoadedDataMappings";
import { serializeSideLoadedDataSet } from "lib/serializers/sideLoadedDataSets";
import { offlineActionCreator, urlCheckAndAddIdAndAction } from "./lib";

const sideLoadedDataUrlCreator = ({
  changesSince,
  id,
  action,
}: URLCreatorProps = {}) =>
  queryString.stringifyUrl({
    url: urlCheckAndAddIdAndAction("/v2/sideloaded-data/", id, action),
    query: {
      changesSince,
    },
  });

export const SideLoadedBusinessAction = offlineActionCreator(
  SIDELOADED_BUSINESS,
  serializeSideLoadedBusiness,
  sideLoadedDataUrlCreator,
  false,
);

// The records only differ by the one field, so the request carries the ids and
// the value rather than a document each. The payload is still per-record, so the
// reducer can apply it optimistically before the response lands.
export const bulkSetSideLoadedBusinessesIgnored = (
  ids: string[],
  isIgnored: boolean,
) => ({
  type: SIDELOADED_BUSINESS.BULK_SET_IGNORED.REQUEST,
  payload: ids.map(id => ({ id, is_ignored: isIgnored })),
  meta: {
    offline: {
      effect: {
        url: "/v2/sideloaded-data/bulk-set-ignored/",
        method: "POST",
        body: JSON.stringify({ ids, is_ignored: isIgnored }),
      },
      commit: {
        type: SIDELOADED_BUSINESS.BULK_SET_IGNORED.SUCCESS,
      },
      rollback: {
        type: SIDELOADED_BUSINESS.BULK_SET_IGNORED.FAILURE,
        ids,
        isIgnored,
      },
    },
  },
});

const sideLoadedDataSetUrlCreator = ({
  changesSince,
  id,
  action,
}: URLCreatorProps = {}) =>
  queryString.stringifyUrl({
    url: urlCheckAndAddIdAndAction("/v2/sideloaded-data-sets/", id, action),
    query: {
      changesSince,
    },
  });

// Don't use .create - a data set is created by the multipart upload in
// sagas/sideLoadedDataSets.
export const SideLoadedDataSetAction = offlineActionCreator(
  SIDELOADED_BUSINESS_SET,
  serializeSideLoadedDataSet,
  sideLoadedDataSetUrlCreator,
  false,
);

const sideLoadedDataMappingUrlCreator = ({
  changesSince,
  id,
  action,
}: URLCreatorProps = {}) =>
  queryString.stringifyUrl({
    url: urlCheckAndAddIdAndAction("/v2/sideloaded-data-mappings/", id, action),
    query: {
      changesSince,
    },
  });

export const SideLoadedDataMappingAction = offlineActionCreator(
  SIDELOADED_DATA_MAPPING,
  serializeSideLoadedDataMapping,
  sideLoadedDataMappingUrlCreator,
  false,
);

interface UploadSideLoadedDataSetOptions {
  name: string;
  file: File;
  // The file's headers, in file order.
  sourceColumns: string[];
  entries: SideLoadedDataMappingEntry[];
  // An existing template to import under, instead of saving these entries.
  templateId?: string | null;
  templateName?: string;
  // entries differ from templateId's own - apply them rather than reuse as-is.
  hasTemplateEdits?: boolean;
}

export const uploadSideLoadedDataSet = ({
  name,
  file,
  sourceColumns,
  entries,
  templateId = null,
  templateName,
  hasTemplateEdits = false,
}: UploadSideLoadedDataSetOptions) => ({
  type: SIDELOADED_BUSINESS_SET.UPLOAD.REQUEST,
  name,
  sourceColumns,
  entries,
  templateId,
  templateName: templateName || name,
  hasTemplateEdits,
  meta: { file },
});

export const uploadSideLoadedDataSetSuccess = () => ({
  type: SIDELOADED_BUSINESS_SET.UPLOAD.SUCCESS,
});

export const uploadSideLoadedDataSetFailure = (errorMessage: string) => ({
  type: SIDELOADED_BUSINESS_SET.UPLOAD.FAILURE,
  errorMessage,
  error: true,
});

// The error outlives the modal it is shown in, so reopening would otherwise
// present the last failure as though it had just happened.
export const resetSideLoadedDataSetUploadError = () => ({
  type: SIDELOADED_BUSINESS_SET.UPLOAD.RESET_ERROR,
});

interface SaveSideLoadedDataSetMappingOptions {
  dataSetId: string;
  // An existing template to update and link; none creates one for this data set.
  mappingId: string | null;
  name: string;
  entries: SideLoadedDataMappingEntry[];
  sourceColumns: string[];
}

export const saveSideLoadedDataSetMapping = (
  options: SaveSideLoadedDataSetMappingOptions,
) => ({
  type: SIDELOADED_BUSINESS_SET.SAVE_MAPPING.REQUEST,
  ...options,
});

export const saveSideLoadedDataSetMappingSuccess = () => ({
  type: SIDELOADED_BUSINESS_SET.SAVE_MAPPING.SUCCESS,
});

export const saveSideLoadedDataSetMappingFailure = (errorMessage: string) => ({
  type: SIDELOADED_BUSINESS_SET.SAVE_MAPPING.FAILURE,
  errorMessage,
  error: true,
});

export const resetSideLoadedDataSetMappingError = () => ({
  type: SIDELOADED_BUSINESS_SET.SAVE_MAPPING.RESET_ERROR,
});

// One pusher event covers both records and data sets, but the socket saga
// dispatches a single action per event - so fan out from here.
export const refreshSideLoadedData = () => ({
  type: SIDELOADED_BUSINESS_SET.REFRESH.FROM_SOCKET,
});
